import React, { useState, useEffect, useCallback } from 'react';
import { Camera, Video, Wifi, WifiOff, ExternalLink, Copy, Check, Plus, Trash2, Edit2, X, Home, Eye, Maximize2, Settings, Loader2 } from 'lucide-react';
import { useAuth } from '../context/UserAuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SecurityCameras = () => {
  const { user } = useAuth();
  const userId = user?.user_id || user?.email || 'default';
  
  const [cameras, setCameras] = useState([]);
  const [go2rtcUrl, setGo2rtcUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [fullscreenCamera, setFullscreenCamera] = useState(null);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCamera, setEditingCamera] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [newCamera, setNewCamera] = useState({
    name: '',
    rtspUrl: '',
    go2rtcStream: '',
    location: '',
    type: 'indoor'
  });

  // Fetch cameras and settings from database
  const fetchCamerasAndSettings = useCallback(async () => {
    if (!userId || userId === 'default') return;
    
    setLoading(true);
    try {
      const [camerasRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/smart-home/cameras?user_id=${userId}`),
        fetch(`${API_URL}/api/smart-home/settings?user_id=${userId}`)
      ]);
      
      if (camerasRes.ok) {
        const camerasData = await camerasRes.json();
        setCameras(camerasData);
      }
      
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setGo2rtcUrl(settingsData.go2rtcUrl || '');
      }
    } catch (error) {
      console.error('Failed to fetch smart home data:', error);
      toast.error('Failed to load cameras');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchCamerasAndSettings();
  }, [fetchCamerasAndSettings]);

  const handleAddCamera = async () => {
    if (!newCamera.name || !newCamera.go2rtcStream) {
      toast.error('Please enter camera name and stream name');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/smart-home/cameras?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newCamera.name,
          go2rtcStream: newCamera.go2rtcStream,
          location: newCamera.location,
          type: newCamera.type,
          rtspUrl: newCamera.rtspUrl
        })
      });

      if (response.ok) {
        const camera = await response.json();
        setCameras([...cameras, camera]);
        setNewCamera({ name: '', rtspUrl: '', go2rtcStream: '', location: '', type: 'indoor' });
        setShowAddModal(false);
        toast.success(`Camera "${camera.name}" added successfully`);
      } else {
        toast.error('Failed to add camera');
      }
    } catch (error) {
      console.error('Failed to add camera:', error);
      toast.error('Failed to add camera');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCamera = async (id) => {
    const camera = cameras.find(c => c.id === id);
    
    try {
      const response = await fetch(`${API_URL}/api/smart-home/cameras/${id}?user_id=${userId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setCameras(cameras.filter(c => c.id !== id));
        toast.success(`Camera "${camera?.name}" removed`);
      } else {
        toast.error('Failed to delete camera');
      }
    } catch (error) {
      console.error('Failed to delete camera:', error);
      toast.error('Failed to delete camera');
    }
  };

  const handleSaveSettings = async () => {
    if (!go2rtcUrl) {
      toast.error('Please enter the go2rtc URL');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/smart-home/settings?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ go2rtcUrl })
      });

      if (response.ok) {
        toast.success('Settings saved!');
        setShowSettingsModal(false);
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyUrl = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    toast.success('RTSP URL copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const openInVLC = (rtspUrl) => {
    // Create a VLC protocol link
    const vlcUrl = `vlc://${rtspUrl}`;
    window.open(vlcUrl, '_blank');
    toast.info('Opening in VLC... Make sure VLC is installed');
  };

  const CameraCard = ({ camera }) => {
    const isOnline = camera.status === 'online';
    const streamUrl = go2rtcUrl && camera.go2rtcStream 
      ? `${go2rtcUrl}/api/stream.html?src=${camera.go2rtcStream}&mode=webrtc,mse,hls,mjpeg`
      : null;
    
    return (
      <div className="bg-[#1a1a2e] border border-gray-700 rounded-xl overflow-hidden hover:border-purple-500/50 transition-all">
        {/* Camera Preview Area - Live Stream */}
        <div className="relative h-56 bg-black">
          {streamUrl ? (
            <iframe
              src={streamUrl}
              className="w-full h-full border-0"
              allow="autoplay; fullscreen"
              title={camera.name}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
              <div className="text-center">
                <Camera size={48} className="mx-auto mb-2 text-gray-600" />
                <p className="text-gray-500 text-sm">Configure go2rtc</p>
                <p className="text-gray-600 text-xs mt-1">Click Settings to setup</p>
              </div>
            </div>
          )}
          
          {/* Status Badge */}
          <div className={`absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
            isOnline ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {isOnline ? <Wifi size={12} /> : <WifiOff size={12} />}
            {isOnline ? 'Live' : 'Offline'}
          </div>

          {/* Camera Type Badge */}
          <div className="absolute top-3 left-3 px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs">
            {camera.type === 'indoor' ? '🏠 Indoor' : '🌳 Outdoor'}
          </div>
          
          {/* Fullscreen Button */}
          {streamUrl && (
            <button
              onClick={() => setFullscreenCamera(camera)}
              className="absolute bottom-3 right-3 p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors"
            >
              <Maximize2 size={16} />
            </button>
          )}
        </div>

        {/* Camera Info */}
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold text-white">{camera.name}</h3>
              {camera.location && (
                <p className="text-gray-400 text-sm">{camera.location}</p>
              )}
            </div>
            <button
              onClick={() => handleDeleteCamera(camera.id)}
              className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            >
              <Trash2 size={16} />
            </button>
          </div>

          {/* RTSP URL */}
          <div className="bg-gray-900/50 rounded-lg p-2 mb-3">
            <p className="text-xs text-gray-500 mb-1">RTSP URL</p>
            <div className="flex items-center gap-2">
              <code className="text-xs text-gray-400 truncate flex-1">
                {camera.rtspUrl.replace(/\/\/.*:.*@/, '//****:****@')}
              </code>
              <button
                onClick={() => handleCopyUrl(camera.rtspUrl, camera.id)}
                className="p-1 text-gray-500 hover:text-white transition-colors"
              >
                {copiedId === camera.id ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => openInVLC(camera.rtspUrl)}
              className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white py-2 px-3 rounded-lg text-sm font-medium transition-colors"
            >
              <Eye size={16} />
              View Stream
            </button>
            <button
              onClick={() => handleCopyUrl(camera.rtspUrl, camera.id)}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              <ExternalLink size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-500/20 rounded-xl">
            <Home size={28} className="text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Smart Home</h1>
            <p className="text-gray-400 text-sm">
              {go2rtcUrl ? `Connected to ${go2rtcUrl}` : 'Configure go2rtc server to view live feeds'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowSettingsModal(true)}
            className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white py-2.5 px-4 rounded-xl font-medium transition-colors"
          >
            <Settings size={20} />
            Settings
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white py-2.5 px-4 rounded-xl font-medium transition-colors"
          >
            <Plus size={20} />
            Add Camera
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 size={48} className="text-purple-400 animate-spin mb-4" />
          <p className="text-gray-400">Loading cameras...</p>
        </div>
      ) : cameras.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {cameras.map(camera => (
            <CameraCard key={camera.id} camera={camera} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="p-6 bg-gray-800/50 rounded-full mb-6">
            <Camera size={48} className="text-gray-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-300 mb-2">No Cameras Added</h2>
          <p className="text-gray-500 mb-6 max-w-md">
            Add your security cameras to monitor them from your dashboard. 
            Supports RTSP streams from Eufy, Ring, Arlo, and other cameras.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white py-2.5 px-5 rounded-xl font-medium transition-colors"
          >
            <Plus size={20} />
            Add Your First Camera
          </button>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-8 bg-[#1a1a2e] border border-gray-700 rounded-xl p-6">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Video size={20} className="text-purple-400" />
          Setup Instructions
        </h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="bg-gray-900/50 rounded-lg p-4">
            <div className="text-purple-400 font-medium mb-2">1. Install go2rtc</div>
            <p className="text-gray-400">Download go2rtc from GitHub and run it on a computer on your network</p>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-4">
            <div className="text-purple-400 font-medium mb-2">2. Configure go2rtc</div>
            <p className="text-gray-400">Add your camera's RTSP URL to go2rtc.yaml and note the stream name</p>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-4">
            <div className="text-purple-400 font-medium mb-2">3. Add to Dashboard</div>
            <p className="text-gray-400">Click Settings to enter your go2rtc URL, then Add Camera with the stream name</p>
          </div>
        </div>
      </div>

      {/* Add Camera Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a2e] border border-gray-700 rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Add Camera</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Camera Name *</label>
                <input
                  type="text"
                  value={newCamera.name}
                  onChange={(e) => setNewCamera({ ...newCamera, name: e.target.value })}
                  placeholder="e.g., Front Door Camera"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm block mb-1.5">go2rtc Stream Name *</label>
                <input
                  type="text"
                  value={newCamera.go2rtcStream}
                  onChange={(e) => setNewCamera({ ...newCamera, go2rtcStream: e.target.value })}
                  placeholder="e.g., eufy_camera"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-white font-mono text-sm focus:outline-none focus:border-purple-500"
                />
                <p className="text-gray-500 text-xs mt-1">The stream name from your go2rtc.yaml config</p>
              </div>

              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Location (Optional)</label>
                <input
                  type="text"
                  value={newCamera.location}
                  onChange={(e) => setNewCamera({ ...newCamera, location: e.target.value })}
                  placeholder="e.g., Front Porch, Living Room"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="text-gray-400 text-sm block mb-1.5">Camera Type</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setNewCamera({ ...newCamera, type: 'indoor' })}
                    className={`flex-1 py-2.5 rounded-lg font-medium transition-colors ${
                      newCamera.type === 'indoor'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    🏠 Indoor
                  </button>
                  <button
                    onClick={() => setNewCamera({ ...newCamera, type: 'outdoor' })}
                    className={`flex-1 py-2.5 rounded-lg font-medium transition-colors ${
                      newCamera.type === 'outdoor'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    🌳 Outdoor
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCamera}
                disabled={saving}
                className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && <Loader2 size={16} className="animate-spin" />}
                Add Camera
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a2e] border border-gray-700 rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Smart Home Settings</h2>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-gray-400 text-sm block mb-1.5">go2rtc Server URL *</label>
                <input
                  type="text"
                  value={go2rtcUrl}
                  onChange={(e) => setGo2rtcUrl(e.target.value)}
                  placeholder="http://192.168.1.140:1984"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-white font-mono text-sm focus:outline-none focus:border-purple-500"
                />
                <p className="text-gray-500 text-xs mt-1">
                  The URL where go2rtc is running (e.g., http://192.168.1.140:1984)
                </p>
              </div>

              <div className="bg-gray-900/50 rounded-lg p-4 mt-4">
                <p className="text-gray-400 text-sm">
                  <strong className="text-purple-400">Your Mac Mini IP:</strong> 192.168.1.140<br />
                  <strong className="text-purple-400">go2rtc URL:</strong> http://192.168.1.140:1984
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="flex-1 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && <Loader2 size={16} className="animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Camera Modal */}
      {fullscreenCamera && go2rtcUrl && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          <div className="flex items-center justify-between p-4 bg-black/50">
            <h2 className="text-white font-semibold">{fullscreenCamera.name}</h2>
            <button
              onClick={() => setFullscreenCamera(null)}
              className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>
          <div className="flex-1">
            <iframe
              src={`${go2rtcUrl}/api/stream.html?src=${fullscreenCamera.go2rtcStream}&mode=webrtc,mse,hls,mjpeg`}
              className="w-full h-full border-0"
              allow="autoplay; fullscreen"
              title={fullscreenCamera.name}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityCameras;
